import {
  NotificationType,
  SupportedOS,
  analyticsService,
  apiService,
} from "@blacknut/javascript-sdk/dist";
import { logD } from "@blacknut/logging/dist";
import {
  Button,
  CircularProgress,
  NotificationComponent,
  Notifications,
  useBackKey,
  useCustomBack,
  usePrevious,
} from "@blacknut/react-sdk/dist";
import {
  AppLayout,
  NETWORK_EVENTS,
  State,
  StorageKey,
  addNotification,
  clearNotification,
  initialize,
  networkService,
  useAnalytics,
  useLayout,
} from "core";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Navigate, Route, Routes } from "react-router-dom-v5-compat";
import GlobalErrorHandler from "src/components/ErrorHandler/GlobalErrorHandler";
import Header from "src/components/Header/Header";
import { HeaderContext } from "src/components/Header/HeaderProvider";
import { MenuContext } from "src/components/Menu/MenuProvider";
import SpeedTestsHandler from "src/components/SpeedTests/SpeedTestsHandler";
import UpdateManager from "src/components/UpdateManager/UpdateManager";
import useDebug from "src/hooks/useDebug";
import useDeeplink from "src/hooks/useDeeplink";
import useDeviceInfo from "src/hooks/useDeviceInfo";
import HomePage from "src/pages/Home/Home";
import { HeaderStyle } from "src/theme/Theme";
import { LOGGING_TAG, isDreiBuild, isM1Build, isTelecallBuild } from "src/utils/Utils";
import { usePreview } from "../components/Modals/PreviewContext";
import useToken from "../hooks/useToken";
import { useBatch } from "../utils/BatchContext";
import nativeBridge from "../utils/NativeBridge";
import useConfig from "../utils/useConfig";
import usePerformanceMonitoring from "../utils/usePerformanceMonitoring";
import { ReactComponent as IconWarn } from "../assets/lg/ic_exclamation.svg";

import {
  MessageModal,
  ModalContextProvider,
  ModalSubscription,
  useModal,
} from "@blacknut/react-sdk";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { CompatRouter, useNavigate } from "react-router-dom-v5-compat";
import "react-simple-keyboard/build/css/index.css";
import SuspenseCircular from "src/components/SuspenseCircular";
import { detectDevice } from "src/services/detectDevice";
import { useTheme } from "src/theme/ThemeProvider";
import { useAppNavigation } from "src/utils/Navigation/useAppNavigation";
import { ScrollContextProvider } from "src/utils/scroll/ScrollProvider";
import Menu from "../components/Menu/Menu";
import { ModalRoutes } from "../components/Modals/ModalContext";
import { useSpatialNavigationDisabler } from "../utils/Navigation";
import {
  AccountPageMemo,
  ActivateGamePageMemo,
  AllBonusPageMemo,
  BonusPageMemo,
  BrowsePageMemo,
  CategoryPageMemo,
  // ConfirmBlacknutPlusPageMemo,
  GameGenrePageMemo,
  GamePageMemo,
  GamePlayPageMemo,
  GamepadPageMemo,
  GamesByCategoryPageMemo,
  LeMagArticlePageMemo,
  LeMagPageMemo,
  LinkPageMemo,
  LoginPageMemo,
  MyListPageMemo,
  OauthPageMemo,
  OfflinePageMemo,
  OnboardingageMemo,
  PYGPageMemo,
  PassPageMemo,
  PreviewPageMemo,
  ProfilePinPageMemo,
  ProfilesPageMemo,
  SearchPageMemo,
  SearchResultsPageMemo,
  SubscribePageMemo,
} from "./routes";
import styles from "./styles.module.scss";
import { useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import useUserChange from "src/utils/useUserChange";
import { TVRouter } from "../utils/Navigation/TVRouter";
import { isTryBuy } from "src/utils/subscription";
import { useSubscribeNotifications } from "src/utils/useSubscribeNotifications";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const Index = () => {
  const menuWillShowListeners = useRef<Map<() => void, () => void>>(new Map()).current;
  const menuWillHideListeners = useRef<Map<() => void, () => void>>(new Map()).current;
  const [menuHidden, setMenuHidden] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const hideMenu = useCallback(() => {
    for (const key of menuWillHideListeners.keys()) {
      key();
    }
    setMenuHidden(true);
  }, [menuWillHideListeners]);

  const showMenu = useCallback(() => {
    for (const key of menuWillShowListeners.keys()) {
      key();
    }
    setMenuHidden(false);
  }, [menuWillShowListeners]);

  const usingLayout = useLayout();
  const isTV = usingLayout === AppLayout.TV;
  const [headerTitle, setHeaderTitle] = useState<string | undefined>();
  const [headerRight, setHeaderRight] = useState<JSX.Element | undefined>();
  const [headerLeft, setHeaderLeft] = useState<JSX.Element | undefined>();
  const [headerStyle, setHeaderStyle] = useState<Partial<HeaderStyle> | undefined>();
  const showHeader = useCallback(() => {
    isTV ? setHeaderHidden(true) : setHeaderHidden(false);
  }, [isTV]);
  const hideHeader = useCallback(() => {
    setHeaderHidden(true);
  }, []);

  useUserChange();
  return (
    <HeaderContext.Provider
      value={{
        setTitle: setHeaderTitle,
        setHeaderRight,
        setHeaderLeft,
        showHeader,
        hideHeader,
        headerHidden,
        setHeaderStyle,
      }}
    >
      <div className={styles.content}>
        <ScrollToTop />

        <Header
          title={headerTitle}
          headerRight={headerRight}
          headerLeft={headerLeft}
          hidden={isTV ? true : headerHidden}
          headerStyle={headerStyle}
        />
        <div className={styles.menuContainer}>
          <MenuContext.Provider
            value={{
              showMenu,
              hideMenu,
            }}
          >
            <Menu
              className={clsx(styles.menu, menuHidden && styles.hidden)}
              hidden={menuHidden}
            />
            <div className={clsx(styles.routes, menuHidden && styles.hidden)} id="main">
              <Routes>
                <Route
                  path="/catalog"
                  element={
                    <SuspenseCircular>
                      <HomePage />
                    </SuspenseCircular>
                  }
                />
                <Route path="/" element={<Navigate to="catalog" replace />} />

                <Route
                  path="/lemag"
                  element={
                    <SuspenseCircular>
                      <LeMagPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/lemag/:id"
                  element={
                    <SuspenseCircular>
                      <LeMagArticlePageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/browse"
                  element={
                    <SuspenseCircular>
                      <BrowsePageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/page/:id"
                  element={
                    <SuspenseCircular>
                      <CategoryPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/link/:pageId/:linkId"
                  element={
                    <SuspenseCircular>
                      <LinkPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/pass/:id"
                  element={
                    <SuspenseCircular>
                      <PassPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/genre/:id"
                  element={
                    <SuspenseCircular>
                      <GameGenrePageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/pages/games/:category/*"
                  element={
                    <SuspenseCircular>
                      <GamesByCategoryPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/game/:id"
                  element={
                    <SuspenseCircular>
                      <GamePageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/game/:gameId/play"
                  element={
                    <SuspenseCircular>
                      <GamePlayPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/game/:gameId/activate/:provider"
                  element={
                    <SuspenseCircular>
                      <ActivateGamePageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <SuspenseCircular>
                      <SearchPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/search/results"
                  element={
                    <SuspenseCircular>
                      <SearchResultsPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/myList"
                  element={
                    <SuspenseCircular>
                      <MyListPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/account/*"
                  element={
                    <SuspenseCircular>
                      <AccountPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/preview"
                  element={
                    <SuspenseCircular>
                      <PreviewPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/plus"
                  element={
                    <SuspenseCircular>
                      <BonusPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/pyg/:id"
                  element={
                    <SuspenseCircular>
                      <PYGPageMemo />
                    </SuspenseCircular>
                  }
                />
                <Route
                  path="/plus/games"
                  element={
                    <SuspenseCircular>
                      <AllBonusPageMemo />
                    </SuspenseCircular>
                  }
                />
              </Routes>
            </div>
          </MenuContext.Provider>
        </div>

        <SpeedTestsHandler />
      </div>
    </HeaderContext.Provider>
  );
};

const Offline = () => {
  const { navigate } = useAppNavigation();
  useEffect(() => {
    networkService.onNetworkEvent().subscribe((e) => {
      if (e === NETWORK_EVENTS.OFFLINE) {
        navigate("/offline", { replace: true });
      } else if (e === NETWORK_EVENTS.ONLINE) {
        navigate("/", { replace: true });
      }
    });
  }, [navigate]);
  return null;
};

const DeeplinkHandler = () => {
  useDeeplink();
  return null;
};

const PUBLIC_PATHES = ["/gamepad", "/oauth/error", "/oauth/success"];
const isPublic = ({ pathname }: { pathname: string }) => {
  return PUBLIC_PATHES.indexOf(pathname) !== -1;
};
const RedirectOnStartup = ({
  tokenFromCommandLine,
}: {
  tokenFromCommandLine?: boolean;
}) => {
  //XXX do not use useLocation here: https://blacknut.atlassian.net/browse/CA-4976
  const { familyToken, layout, authentication } = useSelector(
    (state: State) => state.globalState,
  );
  const { profile } = useSelector((state: State) => state.profilesState);
  const { active: previewActive } = usePreview();

  const { navigate } = useAppNavigation();
  const initialRedirect = useRef(false);

  const dispatch = useDispatch();
  const qs = new URLSearchParams(window.location.search);
  const isSubUpdated = qs.get("su"); // https://blacknut.atlassian.net/browse/CA-4998

  const previousProfile = usePrevious(profile);
  useEffect(() => {
    if (
      initialRedirect.current &&
      previousProfile &&
      !profile &&
      !authentication.inProgress
    ) {
      navigate("/welcome");
    }
  }, [authentication.inProgress, navigate, previousProfile, profile]);

  useEffect(() => {
    if (initialRedirect.current) return;
    initialRedirect.current = true;
    logD(
      LOGGING_TAG,
      "in RedirectOnStartup profile=%o, family token=%o tokenFromCommandLine: %o, preview=%o",
      profile,
      familyToken,
      tokenFromCommandLine,
      previewActive,
    );
    logD(LOGGING_TAG, "location.pathname: %o", location.pathname);

    if (isSubUpdated) {
      addNotification({
        type: NotificationType.SUCCESS,
        time: new Date().getTime(),
        message: "notifications.subscriptionupdated",
      })(dispatch);
    }

    if (previewActive) {
      return;
    }

    if (isPublic(location)) {
      return;
    }
    const newUser = localStorage.getItem(StorageKey.NEW_USER) == "true";

    if ((!profile || newUser) && familyToken) {
      const qs = new URLSearchParams(window.location.search);
      // Passthrought all params except return url which will be given as state in order to be removed on page change
      const oldReturnUrl = qs.get("returnUrl");
      if (oldReturnUrl) {
        qs.delete("returnUrl");
      }
      let returnUrl: string | undefined = oldReturnUrl || location.pathname;
      if (returnUrl === "/" || returnUrl === "/modal" || returnUrl === "/profiles") {
        returnUrl = "/catalog";
      }
      const query = qs ? `?${qs.toString()}` : "";
      const redirect = `/profiles${query}`;
      logD(LOGGING_TAG, "Redirect to %o return=%o", redirect, returnUrl);
      navigate(redirect, { state: { returnUrl }, replace: true });
    } else if (!profile && !familyToken) {
      const qs = new URLSearchParams(window.location.search);
      // Passthrought all params except return url which will be given as state in order to be removed on page change
      const oldReturnUrl = qs.get("returnUrl");
      if (oldReturnUrl) {
        qs.delete("returnUrl");
      }
      const query = qs ? `?${qs.toString()}` : "";
      const redirect = `/welcome${query}`;
      let returnUrl: string | undefined = oldReturnUrl || location.pathname;
      if (returnUrl === "/welcome") {
        returnUrl = undefined;
      }
      logD(LOGGING_TAG, "Redirect (1) to %o", redirect, returnUrl);
      navigate(redirect, { state: { returnUrl }, replace: true });
    } else if (profile) {
      const qs = new URLSearchParams(window.location.search);
      const query = qs ? `?${qs.toString()}` : "";
      let redirect = `${location.pathname}`;
      if (redirect === "" || redirect === "/" || redirect === "/modal") {
        redirect = `/catalog`;
      }
      logD(
        LOGGING_TAG,
        "Redirect (2) to %o (%o,%o)",
        qs.toString(),
        location.pathname,
        redirect,
      );
      navigate(`${redirect}${query}`, { replace: true });
    } else if (tokenFromCommandLine) {
      const qs = new URLSearchParams(window.location.search);
      // Passthrought all params except return url which will be given as state in order to be removed on page change
      const oldReturnUrl = qs.get("returnUrl");
      if (oldReturnUrl) {
        qs.delete("returnUrl");
      }
      const query = qs ? `?${qs.toString()}` : "";
      const redirect = `/profiles${query}`;
      let returnUrl: string | undefined = oldReturnUrl || location.pathname;
      if (returnUrl === "/profiles") {
        returnUrl = undefined;
      }
      logD(LOGGING_TAG, "Redirect (3) to %o %o", redirect, returnUrl);
      navigate(redirect, { state: { returnUrl }, replace: true });
    }
  }, [profile, familyToken, tokenFromCommandLine, layout, previewActive, navigate]);

  return null;
};
const BACK_HANDLER_LOGGING_TAG = "BackHandler";
const BackHandler = () => {
  const { goBack } = useAppNavigation();
  const location = useLocation();

  useBackKey({ goBack });

  const { push: backPush } = useCustomBack();
  const modalSub = useRef<ModalSubscription>();
  const { push: modalPush } = useModal();
  const { t } = useTranslation();
  const locationRef = useRef(location);
  useEffect(() => {
    logD(BACK_HANDLER_LOGGING_TAG, "In use effect", locationRef.current.pathname);

    backPush(() => {
      const isLeavingApp = TVRouter.history
        ? TVRouter.history.location.pathname === "/catalog"
        : (nativeBridge.module?.exit &&
            (window.location.pathname === "/catalog" ||
              (apiService.userAgent.os === "Vidaa" &&
                window.location.pathname === "/profiles"))) ||
          window.location.pathname === "/welcome";

      if (isLeavingApp) {
        const onClose = () => {
          modalSub.current?.remove();
          modalSub.current = undefined;
        };
        const onConfirmed = () => {
          modalSub.current?.remove();
          modalSub.current = undefined;
          nativeBridge.module?.exit?.();
          window.close();
        };
        modalSub.current = modalPush((props) => (
          <MessageModal
            {...props}
            title={t("dialogs.exitApp.title")}
            message={t("dialogs.exitApp.message")}
            onClose={onClose}
            buttons={[
              <Button variant="secondary" key="cancel" onClick={onClose} testID="cancel">
                {t("buttons.cancel")}
              </Button>,
              <Button variant="primary" key="ok" onClick={onConfirmed} testID="confirm">
                {t("buttons.ok")}
              </Button>,
            ]}
          />
        ));
        return true;
      }

      return false;
    });
  }, [backPush, modalPush, t]);

  return null;
};

const SpatialNavigationDisabler = () => {
  useSpatialNavigationDisabler();
  return null;
};

const I18NBlocker = (props: PropsWithChildren<unknown>) => {
  const { i18n } = useTranslation();
  const [i18nInited, setI18nInited] = useState(i18n.isInitialized);
  useEffect(() => {
    if (i18n.isInitialized) {
      setI18nInited(true);
    }
  }, [i18n.isInitialized]);
  if (!i18nInited) return null;
  return <>{props.children}</>;
};
const MainRoutes = ({ tokenFromCommandLine }: { tokenFromCommandLine?: boolean }) => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const { profile } = useSelector((state: State) => state.profilesState);
  return (
    <ModalContextProvider location={location} navigate={navigate}>
      <ScrollContextProvider>
        <SpatialNavigationDisabler />
        <RedirectOnStartup tokenFromCommandLine={tokenFromCommandLine} />
        <GlobalErrorHandler />
        <UpdateManager />
        <Offline />

        <DeeplinkHandler />

        <BackHandler />

        <ModalRoutes>
          <Route
            path="/offline"
            element={
              <SuspenseCircular>
                <OfflinePageMemo />
              </SuspenseCircular>
            }
          />
          <Route
            path="/welcome"
            element={
              <SuspenseCircular>
                <OnboardingageMemo />
              </SuspenseCircular>
            }
          />

          <Route
            path="/signin"
            element={
              <SuspenseCircular>
                <LoginPageMemo />
              </SuspenseCircular>
            }
          />

          <Route
            path="/subscribe"
            element={
              <SuspenseCircular>
                <SubscribePageMemo />
              </SuspenseCircular>
            }
          />

          <Route
            path="/profiles"
            element={
              <SuspenseCircular>
                <ProfilesPageMemo />
              </SuspenseCircular>
            }
          />
          <Route
            path="/profiles/:id/pin"
            element={
              <SuspenseCircular>
                <ProfilePinPageMemo />
              </SuspenseCircular>
            }
          />
          <Route
            path="/preview"
            element={
              <SuspenseCircular>
                <PreviewPageMemo />
              </SuspenseCircular>
            }
          />

          <Route
            path="/gamepad"
            element={
              <SuspenseCircular>
                <GamepadPageMemo />
              </SuspenseCircular>
            }
          ></Route>

          <Route
            path="/oauth/:kind"
            element={
              <SuspenseCircular>
                <OauthPageMemo />
              </SuspenseCircular>
            }
          ></Route>

          {profile && <Route path="/*" element={<Index />} />}
        </ModalRoutes>
      </ScrollContextProvider>
    </ModalContextProvider>
  );
};

const useLocale = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const dir = i18n.dir(i18n.language);
    document.documentElement.dir = dir;
  }, [i18n, i18n.language]);
};
const AppRouter = ({ tokenFromCommandLine }: { tokenFromCommandLine?: boolean }) => {
  const _isM1Build = isM1Build();
  const { theme } = useTheme();
  const { active: spatialNavActive } = useSpatialNavigation();
  const isTV = useLayout() === AppLayout.TV;

  const { appState, user, notifications } = useSelector((state: State) => ({
    appState: state.globalState.app,
    user: state.globalState.user,
    notifications: state.globalState.notifications,
    layout: state.globalState.layout,
  }));
  const { initialized, initializing } = appState;

  const { pauseMobileLanding, resumeMobileLanding } = useBatch();
  const dispatch = useDispatch();
  useEffect(() => {
    if (!initialized) {
      initialize()(dispatch);
    }
  }, [dispatch, initialized]);

  useEffect(() => {
    if (!initialized) {
      pauseMobileLanding();
    } else {
      resumeMobileLanding();
    }
  }, [initialized, pauseMobileLanding, resumeMobileLanding]);

  const { trackAppOpen } = useAnalytics();

  useSubscribeNotifications();

  useConfig();
  useDeviceInfo();
  useDebug();
  usePerformanceMonitoring();
  useLocale();
  useEffect(() => {
    if ((initialized && !_isM1Build) || (_isM1Build && !user?.anonymous)) {
      const locale = apiService.locale || "NA";
      const deviceInfos = detectDevice(navigator.userAgent);

      analyticsService.setDeviceTheme(theme?.name);
      analyticsService.setDeviceBrowser(deviceInfos?.browserName);
      analyticsService.setLang(locale);
      trackAppOpen();
    }
  }, [_isM1Build, initialized, theme, trackAppOpen, user?.anonymous]);

  //FIXME review this, use new behaviour
  useEffect(() => {
    if (initialized && window.parent) {
      window.parent.postMessage(
        JSON.stringify({
          message: "ready",
        }),
        "*",
      );
    }
  }, [initialized]);

  // in case of pin protected profile, the local storage set item is not enougth, since not stored
  const { userToken, familyToken, subscription } = useSelector(
    (state: State) => state.globalState,
  );
  useEffect(() => {
    nativeBridge.module?.setUserToken && nativeBridge.module?.setUserToken(userToken);
  }, [userToken]);
  useEffect(() => {
    nativeBridge.module?.setFamilyToken &&
      nativeBridge.module?.setFamilyToken(familyToken);
  }, [familyToken]);

  useToken();
  if (!initialized || initializing) {
    return <CircularProgress size={36} className={styles.mainLoading} />;
  }
  const Router =
    isTV && apiService.userAgent.os !== SupportedOS.Webos
      ? (TVRouter as any)
      : (BrowserRouter as any); /*Missing className*/

  const _isTryBuy = isTryBuy(subscription.mainEntitlement);

  if (_isM1Build) {
    import("src/style-m1.css");
  } else if (isDreiBuild()) {
    import("src/style-drei.css");
  } else if (isTelecallBuild()) {
    import("src/style-telecall.css");
  }

  return (
    <>
      <div className={clsx([styles.app, spatialNavActive && styles.spatialNavActive])}>
        {initialized && (
          <I18NBlocker>
            <Router className={styles.router}>
              <CompatRouter>
                <MainRoutes tokenFromCommandLine={tokenFromCommandLine}></MainRoutes>
              </CompatRouter>
            </Router>
          </I18NBlocker>
        )}
      </div>
      {_isTryBuy ? (
        <Notifications
          notifications={notifications}
          clearNotification={(n) => clearNotification(n)(dispatch)}
          renderNotification={(n) => {
            if (_isTryBuy && n.title === "TryBuy") {
              const { title, ...withouttitle } = n;
              return (
                <NotificationComponent
                  notification={withouttitle}
                  renderIcon={() => <IconWarn />}
                />
              );
            }
            return <NotificationComponent notification={n} />;
          }}
        />
      ) : (
        <Notifications
          notifications={notifications}
          clearNotification={(n) => clearNotification(n)(dispatch)}
        />
      )}
    </>
  );
};
export default React.memo(AppRouter);
